// Location: frontend/src/user/Pages/Portfolio.jsx
// Updated to fetch dynamic portfolio data

import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize AOS
    const initAOS = () => {
      const elements = document.querySelectorAll('[data-aos]');

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('portfolio-aos-animate');
          }
        });
      });

      elements.forEach(element => {
        observer.observe(element);
      });
    };

    initAOS();
    fetchProjects();
  }, []);

  // ============== Helper to helper URL ==============
  // Ensures URL has http/https protocol
  const formatUrl = (url) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `https://${url}`;
  };

  const fetchProjects = async () => {
    try {
      // Using the public endpoint we created
      const res = await axios.get(`${API_URL}/admin/public/portfolios`);
      if (res.data.success) {
        // Filter only those set to 'show'
        const activeProjects = res.data.data.filter(p => p.show);
        setProjects(activeProjects);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="portfolio">
      {/* Hero Section */}
      <section className="portfolio-hero">
        <div className="portfolio-hero-background">
          <svg className="portfolio-pattern portfolio-pattern-1" viewBox="0 0 200 200">
            <polygon points="100,10 40,198 190,78 10,78 160,198" fill="rgba(255,255,255,0.1)" />
          </svg>
          <svg className="portfolio-pattern portfolio-pattern-2" viewBox="0 0 200 200">
            <path d="M20,20 L180,20 L180,100 L100,180 L20,100 Z" fill="rgba(255,255,255,0.05)" />
          </svg>
          <svg className="portfolio-pattern portfolio-pattern-3" viewBox="0 0 200 200">
            <path d="M100,20 L180,60 L160,140 L40,140 L20,60 Z" fill="rgba(255,255,255,0.08)" />
          </svg>
        </div>

        <div className="portfolio-hero-content" data-aos="fade-up">
          <h1 className="portfolio-hero-title">Portfolio</h1>
          <p className="portfolio-hero-subtitle">
            Transforming digital landscapes with innovative web and mobile solutions
          </p>
          <p className="portfolio-hero-description">
            We craft exceptional digital experiences through cutting-edge technology and creative design.
            From dynamic web applications to scalable e-commerce platforms, our portfolio showcases
            diverse projects built with modern frameworks and technologies.
          </p>

          <div className="portfolio-hero-icon" data-aos="zoom-in" data-aos-delay="300">
            <svg viewBox="0 0 100 100" className="portfolio-infinity-icon">
              <path d="M25 50 C25 30, 40 20, 50 30 C60 20, 75 30, 75 50 C75 70, 60 80, 50 70 C40 80, 25 70, 25 50 Z"
                fill="none" stroke="white" strokeWidth="3" />
            </svg>
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="portfolio-success-stories" id="success-stories">
        <div className="portfolio-container">
          <div className="portfolio-success-content">
            <div className="portfolio-success-text" data-aos="fade-right">
              <h2 className="portfolio-section-title">Our Portfolio & Success Stories</h2>
              <p className="portfolio-success-description">
                We have successfully delivered 100+ projects across industries, helping businesses
                streamline operations and enhance digital presence locally and globally. We have
                done E-commerce websites, Gaming Apps, Management Software, Food Delivery
                apps many more!
              </p>
            </div>
            <div className="portfolio-success-logos" data-aos="fade-left">
              <p className="portfolio-success-stats">
                We have successfully designed and delivered 50+ brand logos across
                various industries, empowering businesses to streamline operations
                and enhance their digital presence both locally and globally.
              </p>
              <div className="portfolio-logos-section">
                <div className="portfolio-logos-container">
                  <img
                    src="/assets/img/portfolio/portfolio_logos.png"
                    alt="Portfolio Client Logos"
                    className="portfolio-logos-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Magazine Covers Section */}
      <section className="portfolio-magazine-covers" id="magazine-covers">
        <div className="portfolio-container">
          <div className="portfolio-magazine-content">
            <div className="portfolio-magazine-text" data-aos="fade-right">
              <p className="portfolio-magazine-description">
                We have successfully designed 30+ Magazine covers for Bollywood celebrities
                and top brands, creating impactful visuals for luxury, lifestyle, fashion,
                and corporate editions.
              </p>
            </div>
            <div className="portfolio-magazine-images" data-aos="fade-left">
              <div className="portfolio-magazine-grid">
                <div className="portfolio-magazine-item">
                  <img src="/assets/img/portfolio/magzine_1.png" alt="Filmfare Magazine" />
                </div>
                <div className="portfolio-magazine-item">
                  <img src="/assets/img/portfolio/magzine_2.png" alt="Grazia Magazine" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UI/UX Solutions Section */}
      <section className="portfolio-uiux-solutions" id="uiux-solutions">
        <div className="portfolio-container">
          <div className="portfolio-uiux-content">
            <div className="portfolio-uiux-images" data-aos="fade-right">
              <div className="portfolio-uiux-showcase">
                <img src="https://images.unsplash.com/photo-1551650975-87deedd944c3?w=500&h=350&fit=crop" alt="Food Delivery App UI" />
              </div>
            </div>
            <div className="portfolio-uiux-text" data-aos="fade-left">
              <p className="portfolio-uiux-description">
                We have successfully designed 100+ UI/UX solutions for websites and mobile apps,
                including prototyping and wireframing. Our expertise spans industries like food delivery,
                e-commerce, management software, fintech, healthtech, edtech, and SaaS platforms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Solutions Section */}
      <section className="portfolio-digital-solutions" id="digital-solutions">
        <div className="portfolio-container">
          <div className="portfolio-digital-content">
            <div className="portfolio-digital-text" data-aos="fade-right">
              <p className="portfolio-digital-description">
                We have successfully designed and developed 100+ digital solutions, including
                platforms like Femme Care AI Tool, HCC Intake, Neqqo.com, Goodvets.com,
                Justtime.ch, PointClub.com, and Greekexperience.com, spanning industries
                from healthtech and e-commerce to pet care and travel.
              </p>
            </div>
            <div className="portfolio-digital-images" data-aos="fade-left">
              <div className="portfolio-digital-showcase">
                <img src="/assets/img/portfolio/project_1.png" alt="Web Development" />
                <img src="/assets/img/portfolio/project_2.png" alt="Mobile App" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects List Section - NOW DYNAMIC */}
      <section className="portfolio-projects-list" id="projects-list">
        <div className="portfolio-container">
          <div className="portfolio-list-content">
            <div className="portfolio-list-text" data-aos="fade-up">
              <h3 className="portfolio-list-title">Here are some of our project's list!</h3>
              <p className="portfolio-list-description">
                We have successfully developed various digital solutions. Check out our detailed list below.
              </p>
            </div>
            <div className="portfolio-list-table" data-aos="fade-up" data-aos-delay="200">
              <div className="portfolio-table-container">
                {loading ? (
                  <div className="text-center py-4 text-white">Loading projects...</div>
                ) : (
                  <table className="portfolio-projects-table">
                    <thead>
                      <tr>
                        <th>Project Name</th>
                        <th>Platform</th>
                        <th>Industry/Category</th>
                        <th>Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map((project, idx) => (
                        <tr key={idx}>
                          <td>{project.name}</td>
                          <td>{project.platform || 'Web/App'}</td>
                          <td>{project.category}</td>
                          <td>
                            {project.url ? (
                              <a href={formatUrl(project.url)} target="_blank" rel="noopener noreferrer" className="portfolio-table-link">View</a>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid Section - NOW DYNAMIC */}
      <section className="portfolio-projects" id="projects">
        <div className="portfolio-container">
          <h2 className="portfolio-section-title" data-aos="fade-up">Featured Projects</h2>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading projects...</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No projects found.</div>
          ) : (
            <div className="portfolio-projects-grid">
              {projects.map((project, index) => (
                <div
                  key={project._id}
                  className="portfolio-project-card"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="portfolio-project-image">
                    <img
                      src={project.image ? `${API_URL}/uploads/${project.image}` : "https://via.placeholder.com/400x300?text=No+Image"}
                      alt={project.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="portfolio-project-overlay">
                      {project.url && (
                        <a href={formatUrl(project.url)} target="_blank" rel="noopener noreferrer" className="portfolio-view-project-btn">
                          View Project
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="portfolio-project-content">
                    <h3 className="portfolio-project-title">{project.name}</h3>
                    <p className="portfolio-project-description">{project.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Portfolio;